import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { Button, LoadingSmall, AlertService, Theme as theme } from '@spoiler-alert/ui-library';
import { TitleService } from '../../services';
import { fetchWithToken } from '../../services/fetch-with-token';
import AppSettings from '../../app-settings';
import UploadKeheItemExclusionsWidget from './exclusion-widget';
import { Breadcrumbs } from '../../store';
import AnalyticsExclusionWidget from './analytics-exclusion-widget';
import DeletePostsWidget from './delete-posts-widget';
import StressTestWidget from './stress-test-widget';
import ReconciliationUploadWidget from './reconciliation-upload-widget';
import ViewImportErrorsWidget from './view-import-errors-widget';
import DataRestoreWidget from './data-restore-widget';

const serverUrl = AppSettings.GRAPHQL_SERVER_URL.replace('admin/', '');
const fullBackup = false;
const checkBackupInterval = 1000 * 60;
// Demo has no backup destination configured, so the backup status endpoint always fails there.
const showBackup = AppSettings.ENVIRONMENT_NAME !== 'demo';
const urls = {
  dbStatus:
    AppSettings.ENVIRONMENT_NAME === 'prod'
      ? `${serverUrl}backup-restore/V2/backup-status`
      : `${serverUrl}backup-restore/backup-status`,
  backupDb:
    AppSettings.ENVIRONMENT_NAME === 'prod'
      ? `${serverUrl}backup-restore/V2/backup`
      : `${serverUrl}backup-restore/backup/${fullBackup}`,
  pauseBackups: `${serverUrl}backup-restore/V2/pause-backups`,
  resetDb: `${serverUrl}data/reset/${AppSettings.DATABASE_RESET_PROFILE}`,
  updateDates: `${serverUrl}data/update-dates`,
  reconciliation: `${serverUrl}import/reconciliation`,
};

const useStyles = createUseStyles({
  backupLoader: {
    display: 'inline-block',
    width: 50,
  },
  backupContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  backupReady: {
    marginLeft: 20,
  },
  resetWrap: {
    marginTop: 30,
  },
  offerInputContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  dataUtilitiesGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  dataUtility: {
    width: '50%',
    minWidth: 500,
    boxSizing: 'border-box',
    padding: [0, 20, 20, 0],
  },
  dataUtilityInner: {
    padding: 15,
    border: `2px ${theme.grey80} solid`,
    borderRadius: 2,
    height: '100%',
    boxShadow: '0 1px 10px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  errorText: {
    color: theme.errorColor,
    fontSize: '14px',
    fontWeight: 500,
    width: '100%',
  },
  button: {
    width: '100%',
  },
});

const DataUtilities = ({ match }) => {
  const classes = useStyles();
  const [enableBackup, setEnableBackup] = useState(false);
  const [backupStatus, setBackupStatus] = useState(false);
  const [enableReset, setEnableReset] = useState(true);
  const [enableDateUpdate, setEnableDateUpdate] = useState(true);

  const intervalCheckRef = useRef();

  const checkDbStatus = async () => {
    const resp = await fetchWithToken(urls.dbStatus, {});
    setBackupStatus(resp?.status);
    if ([200, 304].includes(resp.status)) {
      setEnableBackup(true);
    } else {
      setEnableBackup(false);
    }
  };

  useEffect(() => {
    TitleService.setTitles('Data Utilities Panel');
    Breadcrumbs.set([
      {
        url: '/data',
        title: 'Data Utilities Panel',
      },
    ]);
    if (showBackup) {
      checkDbStatus();
      intervalCheckRef.current = setInterval(checkDbStatus, checkBackupInterval);
    }
    return () => {
      clearInterval(intervalCheckRef.current);
    };
  }, [match.url]);

  const startBackup = async () => {
    const resp = await fetchWithToken(urls.dbStatus, {});
    if ([200, 304].includes(resp.status)) {
      await fetchWithToken(urls.backupDb, { method: 'POST' });
      setEnableBackup(false);
      setBackupStatus(503);
    }
    clearInterval(intervalCheckRef.current);
    intervalCheckRef.current = setInterval(checkDbStatus, 1000 * 30);
  };

  const startReset = () => {
    setEnableReset(false);
    fetchWithToken(urls.resetDb, { method: 'POST' })
      .then((resp) => {
        if (resp.status !== 200) throw new Error('Reset failed');
        setEnableReset(true);
        AlertService.alert({
          type: 'success',
          message: 'Database reset was successful!',
          autoDismiss: true,
          dismissDelay: 5000,
        });
      })
      .catch(() => AlertService.alert({ type: 'warning', message: 'Database reset failed.' }));
  };

  const startDateUpdate = () => {
    setEnableDateUpdate(false);
    fetchWithToken(urls.updateDates, { method: 'POST' })
      .then((resp) => {
        if (resp.status !== 200) throw new Error('Date Update failed');
        setEnableDateUpdate(true);
        AlertService.alert({
          type: 'success',
          message: 'Date update was successful!',
          autoDismiss: true,
          dismissDelay: 5000,
        });
      })
      .catch(() => AlertService.alert({ type: 'warning', message: 'date update failed.' }));
  };

  const toggleBackupRuns = async (pauseBackup) => {
    const response = await fetchWithToken(urls.pauseBackups, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pause: pauseBackup }),
    });
    if (response.status !== 200) {
      AlertService.alert({
        type: 'warning',
        message: 'There was an issue toggling backup runs, please contact a support engineer.',
      });
      return;
    }
    await checkDbStatus();
  };

  const backupButtonText = () => {
    let backupText;
    switch (AppSettings.ENVIRONMENT_NAME) {
      case 'prod':
        backupText = 'Backup PROD data to UAT';
        break;
      case 'uat':
        backupText = 'Backup UAT data to STAGING';
        break;
      default:
        backupText = 'Backup data locally';
        break;
    }
    return backupText;
  };

  const determineBackupStatus = () => {
    if (enableBackup) {
      return (
        <div className={classes.backupContainer}>
          <Button className={classes.button} disabled={!enableBackup} onClick={startBackup}>
            {backupButtonText()}
          </Button>
          {AppSettings.ENVIRONMENT_NAME === 'prod' && (
            <Button className={classes.button} onClick={() => toggleBackupRuns(true)}>
              Pause Backups
            </Button>
          )}
        </div>
      );
    }

    if (backupStatus === 423) {
      return (
        <div className={classes.backupContainer}>
          <span className={classes.errorText}>Backups are currently paused. Please unpause backups to continue.</span>
          {AppSettings.ENVIRONMENT_NAME === 'prod' && (
            <Button className={classes.button} onClick={() => toggleBackupRuns(false)}>
              Unpause Backups
            </Button>
          )}
        </div>
      );
    }

    if (backupStatus === 503) {
      return (
        <div>
          <div className={classes.backupLoader}>
            <LoadingSmall />
          </div>
          Currently backing up
        </div>
      );
    }

    if (backupStatus) {
      return (
        <div className={classes.errorText}>
          There is an issue with the backup process, please reach out to the support engineer.
        </div>
      );
    }

    return <></>;
  };

  return (
    <div>
      <div className={classes.dataUtilitiesGrid}>
        {showBackup && (
          <div className={classes.dataUtility}>
            <div className={classes.dataUtilityInner}>
              <h4>Database Backup</h4>
              {determineBackupStatus()}
            </div>
          </div>
        )}
        {AppSettings.DATABASE_RESET_PROFILE &&
          (AppSettings.ENVIRONMENT_NAME === 'demo' || AppSettings.ENVIRONMENT_NAME === 'staging') && (
            <div className={classes.dataUtility}>
              <div className={classes.dataUtilityInner}>
                <h4>Demo Reset</h4>
                <div className={classes.backupContainer}>
                  <Button className={classes.button} onClick={startReset} disabled={!enableReset}>
                    {AppSettings.ENVIRONMENT_NAME === 'demo'
                      ? 'Reset Demo Environment to Default State'
                      : 'Reset Staging Environment to Demo State'}
                  </Button>
                  {AppSettings.ENVIRONMENT_NAME === 'demo' && (
                    <Button className={classes.button} onClick={startDateUpdate} disabled={!enableDateUpdate}>
                      Update Best By Dates
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        <div className={classes.dataUtility}>
          <div className={classes.dataUtilityInner}>
            <UploadKeheItemExclusionsWidget classes={classes} />
          </div>
        </div>
        <div className={classes.dataUtility}>
          <div className={classes.dataUtilityInner}>
            <ReconciliationUploadWidget uploadUrl={urls.reconciliation} />
            <ViewImportErrorsWidget />
          </div>
        </div>
        <div className={classes.dataUtility}>
          <div className={classes.dataUtilityInner}>
            <AnalyticsExclusionWidget classes={classes} />
          </div>
        </div>
        <div className={classes.dataUtility}>
          <div className={classes.dataUtilityInner}>
            <DeletePostsWidget classes={classes} />
          </div>
        </div>
        {AppSettings.ENVIRONMENT_NAME !== 'prod' && (
          <div className={classes.dataUtility}>
            <div className={classes.dataUtilityInner}>
              <StressTestWidget classes={classes} />
            </div>
          </div>
        )}
        {AppSettings.ENVIRONMENT_NAME !== 'prod' && AppSettings.ENVIRONMENT_NAME !== 'staging' && (
          <div className={classes.dataUtility}>
            <div className={classes.dataUtilityInner}>
              <DataRestoreWidget />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

DataUtilities.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  mutate: PropTypes.func,
  match: PropTypes.object,
};

export default DataUtilities;
